import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import SpinnerOverlay from 'components/SpinnerOverlay';
import { FreeMonthPromoHelper, SubscriptionHelper } from 'view/helpers';
import { FREE_MONTH_PROMO } from 'constants/freeMonthPromo';
import { fetchCurrentSubscription } from 'data/reducers/subscriptionReducer';

import { SiteHeader } from '../components/site/site-header';
import { SiteFooter } from '../components/site/site-footer';
import { FreeMonthPromoModal } from '../components/site/free-month-promo-modal';

import '../styles/marketing.css';

/**
 * Whether the visitor already has a paid subscription and should not see the promo.
 * @param {Object|null} currentSubscription
 * @returns {boolean}
 */
function hasPaidSubscription(currentSubscription) {
	if (!currentSubscription) return false;

	const plan =
		currentSubscription.plan || currentSubscription.subscription?.plan;
	const subscriptionId =
		currentSubscription.id || currentSubscription.subscription?.id;

	if (!subscriptionId) return false;
	if (plan?.name?.toLowerCase().includes('free')) return false;

	return true;
}

// Shell for the public marketing pages (replaces the dashboard AppContainer/AppHeader).
// `.mk-root` scopes every marketing style - see tailwind.marketing.config.js.
function MarketingLayout({ children }) {
	const [promoOpen, setPromoOpen] = useState(false);
	const dispatch = useDispatch();

	const {
		isLoadingAddress,
		isLoadingProperty,
		userId,
		authToken,
		currentSubscription,
		isLoadingCurrentSubscription,
	} = useSelector((state) => ({
		isLoadingAddress: state.address.isLoading,
		isLoadingProperty: state.property.isLoading,
		userId: state.user.userData?.id,
		authToken: state.user.authToken,
		currentSubscription: state.subscription?.currentSubscription,
		isLoadingCurrentSubscription:
			state.subscription?.isLoadingCurrentSubscription,
	}));

	const isLoggedIn = !!authToken;

	// Pricing/dashboard/settings load this; marketing pages do not, so a fresh
	// reload of /, /about, or /features leaves currentSubscription null until we fetch.
	useEffect(() => {
		if (isLoggedIn && !currentSubscription) {
			dispatch(fetchCurrentSubscription());
		}
	}, [dispatch, isLoggedIn, currentSubscription]);

	useEffect(() => {
		// Paid users must be hidden; wait until /current settles before deciding.
		if (isLoggedIn && isLoadingCurrentSubscription) {
			return undefined;
		}

		if (hasPaidSubscription(currentSubscription)) {
			return undefined;
		}

		// Skip the promo while a plan selection is mid-checkout after login/signup.
		if (SubscriptionHelper.getPendingCheckout()) {
			return undefined;
		}

		if (!FreeMonthPromoHelper.shouldShowFreeMonthPromo(userId)) {
			return undefined;
		}

		const timer = setTimeout(() => {
			if (
				SubscriptionHelper.getPendingCheckout() ||
				!FreeMonthPromoHelper.shouldShowFreeMonthPromo(userId)
			) {
				return;
			}
			FreeMonthPromoHelper.markFreeMonthPromoShown(userId);
			setPromoOpen(true);
		}, FREE_MONTH_PROMO.SHOW_DELAY_MS);

		return () => clearTimeout(timer);
	}, [
		userId,
		currentSubscription,
		isLoggedIn,
		isLoadingCurrentSubscription,
	]);

	const closePromo = () => {
		FreeMonthPromoHelper.markFreeMonthPromoDismissed(userId);
		setPromoOpen(false);
	};

	let spinnerText = '';
	if (isLoadingAddress) {
		spinnerText = 'Checking Address...';
	} else if (isLoadingProperty) {
		spinnerText = 'Loading Property...';
	}

	return (
		<div className="mk-root flex min-h-screen flex-col">
			<SiteHeader />
			<SpinnerOverlay
				text={spinnerText}
				isLoading={isLoadingAddress || isLoadingProperty}
			/>
			<main className="flex-1">{children}</main>
			<SiteFooter />
			<FreeMonthPromoModal
				isOpen={promoOpen}
				onClose={closePromo}
				onCta={closePromo}
			/>
		</div>
	);
}

export default MarketingLayout;
